/**
 * push.ts — Web Push 訂閱管理（僅 PWA 用）
 */
import { supabase } from './supabase'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string

/** 是否以加到主畫面的方式開啟（standalone） */
export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
}

/** 瀏覽器是否支援 Web Push */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}


/** 取得目前 SW 的 PushSubscription（沒有則 null） */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.ready
  return reg.pushManager.getSubscription()
}

/**
 * 請求通知權限並訂閱 Push，把訂閱存進 Supabase。
 * 必須由使用者手勢觸發（iOS 規定）。
 */
export async function subscribePush(): Promise<'subscribed' | 'denied' | 'error'> {
  try {
    const perm = await Notification.requestPermission()
    if (perm !== 'granted') return 'denied'

    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: VAPID_PUBLIC_KEY,
    })

    const keys = sub.toJSON().keys as { p256dh: string; auth: string }
    const { error } = await supabase.from('push_subscriptions').upsert(
      {
        endpoint: sub.endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
        device_label: navigator.userAgent.slice(0, 80),
      },
      { onConflict: 'endpoint' }
    )
    if (error) throw error

    return 'subscribed'
  } catch (e) {
    console.error('[push] subscribePush error', e)
    return 'error'
  }
}

/** 取消訂閱並從 Supabase 刪除 */
export async function unsubscribePush(): Promise<void> {
  const sub = await getCurrentSubscription()
  if (!sub) return
  const endpoint = sub.endpoint
  await sub.unsubscribe()
  await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint)
}
